import React from "react";
import Logo from "../assets/logo-applaby.png";

import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function CookiesPage() {
	const { t } = useTranslation();
	return (
		<>
			<Helmet>
				<title>{t("seo.cookies.title")}</title>
				<meta name="description" content={t("seo.cookies.description")} />
				<meta name="robots" content="noindex, follow" />
				<link rel="canonical" href="https://applaby.com/politica-cookies" />
			</Helmet>

			<div className="bg-gray-50 min-h-screen py-12">
				<section className="max-w-4xl mx-auto bg-white shadow-md rounded-xl p-8">
					<a
						href={"/"}
						className="mb-10"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<img
							src={Logo}
							alt="Logo Applaby"
							style={{ height: "40px", objectFit: "contain" }}
						/>
					</a>
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Política de Cookies
					</h1>
					<p className="text-sm text-gray-500 mb-8">
						Actualizado el 24 de septiembre de 2025
					</p>
					<p className="text-gray-700 mb-4">
						En Applaby utilizamos cookies y tecnologías similares para
						garantizar el correcto funcionamiento de nuestra plataforma, mejorar
						tu experiencia de usuario, analizar el tráfico y, en algunos casos,
						mostrarte contenido personalizado.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						1. ¿Qué son las cookies?
					</h2>
					<p className="text-gray-700 mb-4">
						Una cookie es un pequeño archivo de texto que se almacena en tu
						navegador cuando visitas un sitio web. Su finalidad es recordar tus
						preferencias y mejorar tu navegación. Existen cookies de sesión (se
						eliminan al cerrar el navegador) y persistentes (se mantienen en tu
						dispositivo durante un tiempo determinado).
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						2. Tipos de cookies que utilizamos
					</h2>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							<strong>Cookies técnicas o necesarias:</strong> permiten que la
							web funcione correctamente y no se pueden desactivar (ej. inicio
							de sesión, selección de idioma).
						</li>
						<li>
							<strong>Cookies analíticas:</strong> nos ayudan a entender cómo se
							usa la web para mejorarla. Usamos herramientas como Google
							Analytics o Hotjar, siempre con tu consentimiento.
						</li>
					</ul>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						3. Gestión de cookies
					</h2>
					<p className="text-gray-700 mb-4">
						Puedes aceptar o rechazar las cookies a través del banner que
						aparece en tu primera visita. También puedes gestionarlas desde la
						configuración de tu navegador en cualquier momento.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						4. Cookies de terceros
					</h2>
					<p className="text-gray-700 mb-4">
						Algunas cookies son gestionadas por servicios externos que
						utilizamos:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
						<li>
							<strong>Stripe:</strong> para pagos seguros.
						</li>
						<li>
							<strong>Google Analytics:</strong> para análisis de tráfico (con
							consentimiento).
						</li>
						<li>
							<strong>Hotjar:</strong> para estudios de usabilidad (con
							consentimiento).
						</li>
						<li>
							<strong>Google Maps:</strong> algunas funcionalidades de Applaby
							integran la API de Google Maps (como el autocompletado de
							direcciones o la visualización de mapas). Esto implica que Google
							pueda recopilar datos técnicos del usuario (p. ej., dirección IP)
							con fines de seguridad y para garantizar el correcto
							funcionamiento del servicio, conforme a su propia política de
							privacidad.
						</li>
					</ul>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						5. Uso de Hotjar
					</h2>
					<p className="text-gray-700 mb-3">
						Este sitio utiliza <strong>Hotjar</strong>, un servicio de análisis
						web que nos ayuda a comprender mejor la interacción de los usuarios
						con nuestra plataforma. Hotjar registra información relativa al
						comportamiento de navegación, como clics, movimientos del ratón,
						desplazamientos o interacciones con formularios no sensibles. Estos
						datos se utilizan únicamente con fines analíticos para mejorar la
						experiencia de usuario y optimizar nuestros servicios.
					</p>
					<p className="text-gray-700 mb-3">
						Hotjar no recopila información personal identificable ni rastrea la
						actividad fuera de nuestro sitio web. Los datos recopilados se
						almacenan y procesan de conformidad con su{" "}
						<a
							href="https://www.hotjar.com/legal/policies/privacy/"
							target="_blank"
							rel="noopener noreferrer"
							className="text-green-600 hover:underline"
						>
							Política de Privacidad
						</a>
						.
					</p>
					<p className="text-gray-700 mb-3">
						Hotjar únicamente se activa si otorgas tu consentimiento a través de
						nuestro banner de cookies. Asimismo, puedes optar por excluirte del
						seguimiento de Hotjar en cualquier momento visitando{" "}
						<a
							href="https://www.hotjar.com/policies/do-not-track/"
							target="_blank"
							rel="noopener noreferrer"
							className="text-green-600 hover:underline"
						>
							este enlace
						</a>
						.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						6. Duración de las cookies
					</h2>
					<p className="text-gray-700 mb-4">
						Algunas cookies se eliminan automáticamente al cerrar tu navegador
						(cookies de sesión). Otras pueden permanecer en tu dispositivo
						durante un tiempo fijo o hasta que las borres manualmente (cookies
						persistentes).
					</p>
					<p>
						Las cookies analíticas de Google Analytics pueden permanecer en tu
						dispositivo entre 1 minuto y 2 años, dependiendo de la configuración
						y salvo que las elimines manualmente.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						7. ¿Cómo aceptar, rechazar o revocar cookies?
					</h2>
					<p className="text-gray-700 mb-4">
						Puedes cambiar tu consentimiento en cualquier momento accediendo a
						nuestro banner de cookies o configurando tu navegador para
						bloquearlas. Ten en cuenta que desactivar algunas cookies puede
						afectar al funcionamiento de la web.
					</p>

					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						8. Cambios en esta política de cookies
					</h2>
					<p className="text-gray-700 mb-3">
						Podemos actualizar esta Política de Cookies para reflejar cambios en
						el uso de cookies u obligaciones legales. Te informaremos de
						cualquier modificación relevante mediante un aviso en la web o a
						través del banner de cookies. El uso continuado de la web tras la
						notificación de cambios implicará la aceptación de la nueva
						política.
					</p>
					<p className="text-gray-700 mb-4">
						Para más información sobre cómo tratamos tus datos personales,
						consulta nuestra{" "}
						<a
							href="/politica-privacidad"
							className="text-green-600 hover:underline"
						>
							Política de Privacidad
						</a>
						.
					</p>
					<h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
						9. Responsable del tratamiento
					</h2>
					<p className="text-gray-700 mb-4">
						El responsable del tratamiento de los datos obtenidos a través de
						las cookies es Yowlu SL (Applaby), con domicilio en Barcelona
						(España). Puedes ponerte en contacto con nosotros a través de{" "}
						<a
							href="mailto:info@applaby.com"
							className="text-green-600 hover:underline"
						>
							info@applaby.com
						</a>
						.
					</p>
				</section>
			</div>
		</>
	);
}
